const mediaSliders = new WeakMap();

const createMediaSlider = (section) => {
  mediaSliders.get(section)?.destroy();

  const track = section.querySelector('[data-media-slider-track]');
  const viewport = section.querySelector('[data-media-slider-viewport]');
  if (!track || !viewport) return;

  const isMobile = window.innerWidth < 750;
  const inactiveClass = isMobile ? 'media-slider-desktop' : 'media-slider-mobile';
  section.querySelectorAll(`.media-slider-video-wrap.${inactiveClass} video`).forEach((v) => v.remove());

  const originals = [...track.querySelectorAll(':scope > [data-media-slider-slide]')];
  if (!originals.length) return;

  const controller = new AbortController();
  const { signal } = controller;
  const count = originals.length;

  let slideStep = 0;
  let offset = 0;
  let dragging = false;
  let wasDragged = false;
  let dragPointer = null;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragStartOffset = 0;
  let dragDirectionLocked = false;
  let lastMoveX = 0;
  let lastMoveTime = 0;
  let velocity = 0;
  let momentumRaf = 0;
  let momentumTime = 0;

  const render = () => track.style.setProperty('--media-slider-offset', `${offset}px`);

  let infiniteEnabled = false;

  const build = () => {
    for (const c of track.querySelectorAll(':scope > [data-media-slider-clone]')) c.remove();

    const slideWidth = originals[0].offsetWidth;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    slideStep = slideWidth + gap;
    if (!slideStep) return;

    const visibleCount = Math.ceil(viewport.clientWidth / slideStep);

    if (count <= visibleCount) {
      infiniteEnabled = false;
      track.classList.add('no-infinite');
      offset = 0;
      render();
      return;
    }

    track.classList.remove('no-infinite');

    infiniteEnabled = true;

    const makeClone = (original) => {
      const clone = original.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-media-slider-clone', '');
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      for (const v of clone.querySelectorAll('video')) {
        v.muted = true;
        v.preload = 'none';
      }
      for (const el of clone.querySelectorAll('a, button, input, select, textarea, [tabindex]')) {
        el.setAttribute('tabindex', '-1');
      }
      return clone;
    };

    const minBuffer = visibleCount + 2;
    const prependCount = Math.ceil(minBuffer / count) * count;
    const appendCount = prependCount;

    const preFrag = document.createDocumentFragment();
    for (let i = prependCount; i > 0; i--) {
      preFrag.append(makeClone(originals[((count - (i % count)) % count)]));
    }
    track.prepend(preFrag);

    const postFrag = document.createDocumentFragment();
    for (let i = 0; i < appendCount; i++) {
      postFrag.append(makeClone(originals[i % count]));
    }
    track.append(postFrag);

    offset = prependCount * slideStep - (viewport.clientWidth - slideWidth) / 2;
    applyOffset();
  };

  const applyOffset = () => {
    if (!slideStep) { render(); return; }

    if (infiniteEnabled) {
      while (offset > slideStep) {
        track.append(track.firstElementChild);
        offset -= slideStep;
        if (dragging) dragStartOffset -= slideStep;
      }

      while (offset < 0) {
        track.prepend(track.lastElementChild);
        offset += slideStep;
        if (dragging) dragStartOffset += slideStep;
      }
    } else {
      const totalWidth = count * slideStep - (parseFloat(getComputedStyle(track).gap) || 0);
      const maxOffset = Math.max(0, totalWidth - viewport.clientWidth);
      offset = Math.max(0, Math.min(offset, maxOffset));
    }

    render();
  };

  build();

  const stopMomentum = () => {
    if (!momentumRaf) return;
    cancelAnimationFrame(momentumRaf);
    momentumRaf = 0;
    momentumTime = 0;
    track.classList.remove('is-animating');
  };

  const momentumStep = (now) => {
    const elapsed = momentumTime ? Math.min(now - momentumTime, 50) : 0;
    momentumTime = now;
    if (elapsed > 0) {
      offset += velocity * elapsed;
      velocity *= 0.94 ** (elapsed / 16);
      applyOffset();
    }
    if (Math.abs(velocity) < 0.02) {
      velocity = 0;
      stopMomentum();
      return;
    }
    momentumRaf = requestAnimationFrame(momentumStep);
  };

  viewport.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    stopMomentum();
    dragging = true;
    wasDragged = false;
    dragDirectionLocked = false;
    dragPointer = e.pointerId;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    dragStartOffset = offset;
    lastMoveX = e.clientX;
    lastMoveTime = e.timeStamp;
    velocity = 0;
    viewport.classList.add('is-dragging');
    track.classList.add('is-animating');
  }, { signal });

  viewport.addEventListener('pointermove', (e) => {
    if (!dragging || e.pointerId !== dragPointer) return;
    const dx = e.clientX - dragStartX;
    const dy = e.clientY - dragStartY;

    if (!dragDirectionLocked) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      if (Math.abs(dy) >= Math.abs(dx)) {
        dragging = false;
        dragPointer = null;
        velocity = 0;
        viewport.classList.remove('is-dragging');
        track.classList.remove('is-animating');
        return;
      }
      dragDirectionLocked = true;
      wasDragged = true;
      if (e.pointerType === 'mouse') viewport.setPointerCapture(e.pointerId);
    }

    const elapsed = e.timeStamp - lastMoveTime;
    if (elapsed > 0) {
      const instant = Math.max(-4, Math.min(4, -(e.clientX - lastMoveX) / elapsed));
      velocity += (instant - velocity) * 0.3;
      lastMoveX = e.clientX;
      lastMoveTime = e.timeStamp;
    }

    offset = dragStartOffset - dx;
    applyOffset();
  }, { signal });

  const onPointerUp = (e) => {
    if (!dragging || e.pointerId !== dragPointer) return;
    dragging = false;
    dragPointer = null;
    if (viewport.hasPointerCapture?.(e.pointerId)) viewport.releasePointerCapture(e.pointerId);
    viewport.classList.remove('is-dragging');

    if (e.timeStamp - lastMoveTime > 100) velocity = 0;
    if (velocity === 0) {
      track.classList.remove('is-animating');
      return;
    }
    stopMomentum();
    momentumTime = 0;
    track.classList.add('is-animating');
    momentumRaf = requestAnimationFrame(momentumStep);
  };

  viewport.addEventListener('pointerup', onPointerUp, { signal });
  viewport.addEventListener('pointercancel', onPointerUp, { signal });
  viewport.addEventListener('wheel', (e) => {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    let delta = e.deltaX;
    if (e.deltaMode === 1) delta *= 16;
    else if (e.deltaMode === 2) delta *= viewport.clientWidth;
    e.preventDefault();
    stopMomentum();
    offset += delta;
    applyOffset();
  }, { passive: false, signal });

  viewport.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

  section.addEventListener('click', (e) => {
    if (wasDragged) {
      wasDragged = false;
      e.preventDefault();
      return;
    }

    const btn = e.target.closest('[data-media-slider-play-pause]');
    if (!btn) return;

    const videoWrap = btn.closest('.media-slider-video-wrap');
    if (!videoWrap) return;

    const video = videoWrap.querySelector('video');
    if (!video) return;

    e.preventDefault();
    e.stopPropagation();

    const pausing = !video.paused;
    if (pausing) {
      video.pause();
      videoWrap.classList.add('is-paused');
      btn.setAttribute('aria-label', 'Play video');
    } else {
      video.play();
      videoWrap.classList.remove('is-paused');
      btn.setAttribute('aria-label', 'Pause video');
    }
  }, { signal });

  const observer = new IntersectionObserver((entries) => {
    if (!entries[0].isIntersecting) return;
    observer.disconnect();
    track.querySelectorAll('video').forEach((v) => {
      v.preload = 'auto';
      v.play();
    });
  }, { threshold: 0 });

  observer.observe(section);

  window.addEventListener('load', () => {
    if (!slideStep) build();
  }, { once: true, signal });

  const destroy = () => {
    controller.abort();
    observer.disconnect();
    stopMomentum();
    for (const c of track.querySelectorAll(':scope > [data-media-slider-clone]')) c.remove();
    viewport.classList.remove('is-dragging');
    track.classList.remove('is-animating');
    track.style.removeProperty('--media-slider-offset');
    mediaSliders.delete(section);
  };

  mediaSliders.set(section, { destroy });
};

const initAll = (root = document) => {
  root.querySelectorAll('.media-slider').forEach((section) => createMediaSlider(section));
};

document.addEventListener('DOMContentLoaded', () => initAll());
document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('.media-slider').forEach((section) => mediaSliders.get(section)?.destroy());
});
